import type { ReactNode } from 'react';
import { Heading, Text } from 'frosted-ui';
import { formatCurrency } from '../utils/currency';

export type OfferInterval = 'week' | 'month' | 'year' | 'one_time';

const INTERVAL_SUFFIX: Record<OfferInterval, string | null> = {
  week: '/ week',
  month: '/ month',
  year: '/ year',
  one_time: null,
};

interface MemberOfferCardProps {
  /** The seller's name, above the headline. */
  sellerName?: string | null;
  productName: string;
  /** From the paid product's description; nothing is shown when empty. */
  benefits?: string[];
  /** Shown instead of benefits (the wizard preview doesn't know them yet). */
  benefitsNote?: string;
  price: number; // minor units
  currency: string;
  interval: OfferInterval;
  trialDays?: number;
  initialFee?: number | null;
  /** The Upgrade button, or whatever replaces it after a purchase. */
  action: ReactNode;
  children?: ReactNode; // under the footnote: errors, fallbacks
}

/**
 * The upgrade offer a free member sees in the "Upgrade" tab, shared by the
 * live experience view and the wizard's member preview. Mobile first: it
 * lives in Whop's iOS/Android webviews.
 */
export default function MemberOfferCard({
  sellerName,
  productName,
  benefits = [],
  benefitsNote,
  price,
  currency,
  interval,
  trialDays = 0,
  initialFee,
  action,
  children,
}: MemberOfferCardProps) {
  const suffix = INTERVAL_SUFFIX[interval];
  const recurring = interval !== 'one_time';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-5)',
        border: '1px solid var(--gray-a5)',
        backgroundColor: 'var(--color-panel-solid)',
      }}
    >
      <Text
        size="1"
        weight="bold"
        style={{
          color: 'var(--accent-11)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          overflowWrap: 'anywhere',
        }}
      >
        {sellerName || 'Upgrade'}
      </Text>
      <Heading size="6" as="h1" style={{ margin: 0, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
        Unlock {productName || 'the paid plan'}
      </Heading>

      {benefits.length > 1 ? (
        <ul
          style={{
            margin: 0,
            paddingLeft: 18,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
          }}
        >
          {benefits.map((b) => (
            <li key={b}>
              <Text size="3" style={{ color: 'var(--gray-12)', lineHeight: 1.55 }}>
                {b}
              </Text>
            </li>
          ))}
        </ul>
      ) : benefits.length === 1 ? (
        <Text size="3" style={{ color: 'var(--gray-12)', lineHeight: 1.55 }}>
          {benefits[0]}
        </Text>
      ) : benefitsNote ? (
        <Text size="2" color="gray">
          {benefitsNote}
        </Text>
      ) : null}

      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
          <Text size="8" weight="bold" className="vara2-tabular" style={{ lineHeight: 1 }}>
            {formatCurrency(price, currency)}
          </Text>
          <Text size="3" color="gray">
            {suffix ?? 'one-time'}
          </Text>
        </div>
        {(trialDays > 0 || (initialFee ?? 0) > 0) && (
          <Text size="2" color="gray" style={{ display: 'block', marginTop: 'var(--space-1)' }}>
            {[
              trialDays > 0 ? `${trialDays}-day free trial` : null,
              (initialFee ?? 0) > 0 ? `${formatCurrency(initialFee ?? 0, currency)} setup fee` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        )}
      </div>

      {action}

      <Text size="2" color="gray" style={{ textAlign: 'center' }}>
        {recurring ? 'Checkout by Whop · cancel anytime' : 'Checkout by Whop'}
      </Text>
      {children}
    </div>
  );
}
